import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckCircle2, XCircle, X } from 'lucide-react';
import { NotificationOutcomeData } from 'types/notifications';

interface NotificationOutcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: NotificationOutcomeData | null;
}

// Read-only detail popup for attendance/regularization notifications that are already resolved
// (approved, rejected, recorded, superseded) — nothing to review here, just what happened.
const NotificationOutcomeModal: React.FC<NotificationOutcomeModalProps> = ({ isOpen, onClose, data }) => {
  if (!data) return null;

  const isSuccess = data.status === 'success';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-[#1e212b] border border-primary-subtle p-6 text-left align-middle shadow-xl transition-all relative">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-neutral-medium hover:text-neutral transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full shrink-0 ${isSuccess ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    {isSuccess ? (
                      <CheckCircle2 className="w-6 h-6 text-green-500" />
                    ) : (
                      <XCircle className="w-6 h-6 text-red-500" />
                    )}
                  </div>
                  <div className="pt-1">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-neutral">
                      {data.title}
                    </Dialog.Title>
                    <div className="mt-2 text-sm text-neutral-medium">{data.message}</div>
                  </div>
                </div>

                <div className="mt-8 flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 font-medium text-sm text-white bg-[#2b3040] hover:bg-[#383f55] rounded-lg transition-colors"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default NotificationOutcomeModal;
